import { useMemo } from "react";
import { cn } from "@/lib/utils";

interface DiffLine {
  type: "unchanged" | "added" | "removed";
  lineNumber: number | null;
  content: string;
}

interface DiffViewerProps {
  original: string;
  modified: string;
  language: string;
}

// Maximum lines to diff before showing a simplified view
const MAX_DIFF_LINES = 500;

/**
 * Simple diff algorithm that compares lines.
 * For very large files, returns a simplified message to avoid performance issues.
 */
function computeDiff(original: string, modified: string): DiffLine[] {
  const originalLines = original.split("\n");
  const modifiedLines = modified.split("\n");

  // Optimize for large files to prevent O(n²) memory/time issues
  if (originalLines.length > MAX_DIFF_LINES || modifiedLines.length > MAX_DIFF_LINES) {
    return [
      { type: "unchanged", lineNumber: 1, content: "// File too large for inline diff view" },
      { type: "unchanged", lineNumber: 2, content: `// Original: ${originalLines.length} lines, Modified: ${modifiedLines.length} lines` },
    ];
  }
  const result: DiffLine[] = [];

  // Simple LCS-based diff
  const lcs = computeLCS(originalLines, modifiedLines);

  let origIdx = 0;
  let modIdx = 0;
  let lcsIdx = 0;

  while (origIdx < originalLines.length || modIdx < modifiedLines.length) {
    if (
      lcsIdx < lcs.length &&
      origIdx < originalLines.length &&
      modIdx < modifiedLines.length &&
      originalLines[origIdx] === lcs[lcsIdx] &&
      modifiedLines[modIdx] === lcs[lcsIdx]
    ) {
      // Unchanged line
      result.push({
        type: "unchanged",
        lineNumber: modIdx + 1,
        content: modifiedLines[modIdx],
      });
      origIdx++;
      modIdx++;
      lcsIdx++;
    } else if (
      origIdx < originalLines.length &&
      (lcsIdx >= lcs.length || originalLines[origIdx] !== lcs[lcsIdx])
    ) {
      // Removed line
      result.push({
        type: "removed",
        lineNumber: null,
        content: originalLines[origIdx],
      });
      origIdx++;
    } else if (
      modIdx < modifiedLines.length &&
      (lcsIdx >= lcs.length || modifiedLines[modIdx] !== lcs[lcsIdx])
    ) {
      // Added line
      result.push({
        type: "added",
        lineNumber: modIdx + 1,
        content: modifiedLines[modIdx],
      });
      modIdx++;
    }
  }

  return result;
}

/**
 * Compute Longest Common Subsequence of two arrays.
 */
function computeLCS(a: string[], b: string[]): string[] {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array(m + 1)
    .fill(null)
    .map(() => Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Backtrack to find LCS
  const lcs: string[] = [];
  let i = m;
  let j = n;
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) {
      lcs.unshift(a[i - 1]);
      i--;
      j--;
    } else if (dp[i - 1][j] > dp[i][j - 1]) {
      i--;
    } else {
      j--;
    }
  }

  return lcs;
}

export function DiffViewer({ original, modified }: DiffViewerProps) {
  const diffLines = useMemo(
    () => computeDiff(original, modified),
    [original, modified]
  );

  const hasChanges = diffLines.some((line) => line.type !== "unchanged");

  if (!hasChanges) {
    return (
      <div className="rounded-lg border bg-muted/30 p-8 text-center">
        <p className="text-muted-foreground">
          No changes made yet. Edit the code to see the diff.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border overflow-hidden bg-[var(--code-bg)]">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse font-mono text-sm">
          <tbody>
            {diffLines.map((line, index) => (
              <tr
                key={index}
                className={cn(
                  line.type === "added" &&
                    "bg-[var(--code-add-bg)] border-l-4 border-l-[var(--code-add-border)]",
                  line.type === "removed" &&
                    "bg-[var(--code-remove-bg)] border-l-4 border-l-[var(--code-remove-border)]"
                )}
              >
                {/* Line number gutter */}
                <td
                  className={cn(
                    "w-12 px-3 py-0.5 text-right select-none",
                    "bg-[var(--code-gutter)] text-[var(--code-gutter-text)]",
                    "border-r border-border"
                  )}
                >
                  {line.type === "removed" ? (
                    <span className="text-[var(--code-remove-border)]">−</span>
                  ) : line.type === "added" ? (
                    <span className="text-[var(--code-add-border)]">+</span>
                  ) : (
                    line.lineNumber
                  )}
                </td>
                {/* Code content */}
                <td className="px-4 py-0.5 whitespace-pre">
                  {line.content || " "}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
